import firebase from "config/firebase";
import {FirebaseError} from "firebase/app";
import {confirmPasswordReset} from "firebase/auth";
import {handleError} from "helpers/errors";
import {FC, useState} from "react";
import {Navigate, useNavigate} from "react-router";
import {useSearchParams} from "react-router-dom";

const ResetPasswordPage: FC = () => {
  const [sp] = useSearchParams();
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [changed, setChanged] = useState(false);
  const navigate = useNavigate();
  const code = sp.get("code");

  const onSubmit = () => {
    if (submitting) return;
    if (!password) return handleError("Please enter a password");
    if (password !== confirmPassword) return handleError("Passwords do not match");
    setSubmitting(true);
    confirmPasswordReset(firebase.auth, code!, password)
      .then(() => setChanged(true))
      .catch((err: FirebaseError) => {
        switch (err.code) {
          case "auth/expired-action-code":
            handleError("This link has expired");
            break;
          case "auth/invalid-action-code":
            handleError("This link is invalid");
            break;
          case "auth/weak-password":
            handleError("Password is too weak");
            break;
          default:
            handleError("An unknown error occurred");
        }
        setSubmitting(false);
      });
  };

  if (!code) return <Navigate to="/auth" replace />;

  return (
    <div className="w-full bg-brand-cream h-full overflow-y-auto flex justify-center items-center">
      <div className="bg-white rounded-2xl divide-y divide-brand-border flex-cool h-max my-12 mx-4 max-w-[768px] min-w-[512px]">
        <header className="flex items-center justify-start gap-2.5 p-5">
          <img src="/akadian-logo-no-tagline.png" alt="Akadian" className="h-8 w-auto mb-4" />
          <span className="font-semibold text-xl text-brand-navy">
            | {changed ? "Password Changed" : "Reset Password"}
          </span>
        </header>
        <main className="flex-cool p-5 gap-4">
          {changed ? (
            <span className="text-sm text-brand-navy font-medium">
              Your password has been reset. You can now sign in with your new password.
            </span>
          ) : (
            <>
              <div className="relative">
                <input
                  type={passwordVisible ? "text" : "password"}
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  className="w-full"
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-brand-muted cursor-pointer select-none"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  {passwordVisible ? "Hide" : "Show"}
                </span>
              </div>
              <div className="relative">
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                  className="w-full"
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-brand-muted cursor-pointer select-none"
                  onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                >
                  {confirmPasswordVisible ? "Hide" : "Show"}
                </span>
              </div>
            </>
          )}
        </main>
        <footer className="p-5 flex items-center justify-between gap-2.5">
          <span
            className="text-sm text-brand-amber hover:underline font-medium cursor-pointer"
            onClick={() => navigate("/auth")}
          >
            Sign In
          </span>
          {!changed && (
            <button disabled={submitting} onClick={onSubmit} className="btn-primary">
              Reset Password
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
